import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Clock } from "lucide-react";
import Shell from "@/pages/Shell";
import { Seo } from "@/components/Seo";
import { api, formatDate } from "@/lib/api";
import { Overline } from "@/components/Reveal";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    api
      .get(`/blog/${slug}`)
      .then((r) => {
        if (!active) return;
        setPost(r.data);
        setStatus("ready");
      })
      .catch(() => active && setStatus("error"));
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <Shell>
      {status === "ready" && post && (
        <>
          <Seo
            title={`${post.title} — Charles McKuhn`}
            description={post.excerpt}
            path={`/blog/${slug}`}
            type="article"
          />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: post.title,
              description: post.excerpt,
              datePublished: post.date,
              articleSection: post.category,
              author: {
                "@type": "Person",
                name: "Charles McKuhn",
                alternateName: "Charles A. McKuhn Jr.",
              },
              publisher: {
                "@type": "Organization",
                name: "Charles McKuhn Ministries",
              },
              mainEntityOfPage: `https://www.charlesmckuhn.com/blog/${slug}`,
            })}
          </script>

          <article className="relative py-20 sm:py-28 bg-parchment dark:bg-ink">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link
                to="/blog"
                data-testid="back-to-journal"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gold-deep dark:text-gold-light hover:underline underline-offset-4"
              >
                <ArrowLeft size={16} /> Back to Journal
              </Link>
              <div className="mt-8">
                <Overline>{post.category}</Overline>
                <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold leading-tight tracking-tight">
                  {post.title}
                </h1>
                <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground font-mono">
                  <span>{formatDate(post.date)}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} /> {post.read_time}
                  </span>
                </div>
              </div>
              <div className="mt-10 space-y-5 text-base sm:text-lg leading-relaxed text-foreground/90">
                {post.body.map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>
          </article>
        </>
      )}

      {status === "loading" && (
        <div className="min-h-[50vh] flex items-center justify-center text-gold-deep dark:text-gold-light">
          <Loader2 className="animate-spin" size={28} />
        </div>
      )}

      {status === "error" && (
        <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-center px-4">
          <p className="text-lg text-muted-foreground">
            That writing could not be found.
          </p>
          <Link
            to="/blog"
            className="px-6 py-3 rounded-full bg-gold text-ink font-semibold hover:bg-gold-light transition-colors"
          >
            Return to the Journal
          </Link>
        </div>
      )}
    </Shell>
  );
}
